/**
 * Format a number as a price with thousands separators, locale-aware.
 * When language is 'fa' (default), uses Persian digits and Persian thousands separator.
 * When language is 'en', uses English digits and standard comma separator.
 * @param {number} price - The price to format
 * @param {string} [language='fa'] - The language code ('fa' or 'en')
 * @returns {string} - The formatted price (number only, no currency label)
 */
export const formatPrice = (price, language = 'fa') => {
    if (typeof price !== 'number' || isNaN(price)) {
        return language === 'fa' ? '۰' : '0';
    }

    const locale = language === 'fa' ? 'fa-IR' : 'en-US';
    return new Intl.NumberFormat(locale).format(Math.round(price));
};

/**
 * Format a price with currency label (Toman), locale-aware.
 * FA: "۲٬۵۰۰٬۰۰۰ تومان"
 * EN: "2,500,000 Toman"
 * @param {number} price - The price to format
 * @param {string} [language='fa'] - The language code ('fa' or 'en')
 * @param {string} [currency='toman'] - The currency type ('toman' or 'tl')
 * @returns {string} - The formatted price with currency label
 */
export const formatCurrency = (price, language = 'fa', currency = 'toman') => {
    const formattedNumber = formatPrice(price, language);

    const labels = {
        toman: language === 'fa' ? 'تومان' : 'Toman',
        tl: 'TL',
    };

    const label = labels[currency] || labels.toman;
    return `${formattedNumber} ${label}`;
};

/**
 * Format a date in Persian format
 * @param {string|Date} date - The date to format
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} - The formatted date
 */
export const formatDate = (date, options = {}) => {
    if (!date) return '';

    const dateObj = typeof date === 'string' ? new Date(date) : date;

    // Default options
    const defaultOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        ...options
    };

    return new Intl.DateTimeFormat('fa-IR', defaultOptions).format(dateObj);
};

/**
 * Format a date as relative time (e.g., "3 days ago")
 * @param {string|Date} date - The date to format
 * @returns {string} - The relative time
 */
export const formatRelativeTime = (date) => {
    if (!date) return '';

    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffInSeconds = Math.floor((now - dateObj) / 1000);

    // Define time intervals in seconds
    const intervals = {
        year: 31536000,
        month: 2592000,
        week: 604800,
        day: 86400,
        hour: 3600,
        minute: 60,
        second: 1
    };

    // Persian units
    const persianUnits = {
        year: 'سال',
        month: 'ماه',
        week: 'هفته',
        day: 'روز',
        hour: 'ساعت',
        minute: 'دقیقه',
        second: 'ثانیه'
    };

    // Find the appropriate interval
    for (const [unit, secondsInUnit] of Object.entries(intervals)) {
        const value = Math.floor(diffInSeconds / secondsInUnit);

        if (value >= 1) {
            return `${value} ${persianUnits[unit]} پیش`;
        }
    }

    return 'همین الان';
};

/**
 * Truncate a string to a specified length and add ellipsis if needed
 * @param {string} text - The text to truncate
 * @param {number} length - The maximum length
 * @returns {string} - The truncated text
 */
export const truncateText = (text, length = 100) => {
    if (!text || text.length <= length) {
        return text;
    }

    return text.substring(0, length) + '...';
};

/**
 * Convert a Snake Case string to Title Case
 * @param {string} text - The snake case text
 * @returns {string} - The title case text
 */
export const snakeToTitleCase = (text) => {
    if (!text) return '';

    return text
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

/**
 * Formats a number with Persian digits
 * @param {number} num - The number to format
 * @returns {string} - The formatted number with Persian digits
 */
export const toPersianDigits = (num) => {
    if (num === null || num === undefined) return '';

    const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    return num.toString().replace(/\d/g, x => persianDigits[x]);
};

/**
 * Format a file size in a human-readable format
 * @param {number} bytes - Size in bytes
 * @returns {string} - Formatted size (e.g., "1.2 MB")
 */
export const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 بایت';

    const units = ['بایت', 'کیلوبایت', 'مگابایت', 'گیگابایت', 'ترابایت'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));

    return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + units[i];
};